import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createRef } from 'react';
import { ComponentSync, LL_H_C, ModuleFE_Toaster, TS_PropRenderer } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_DiseaseProfile } from '@app/dp-frontend';
import { asArray, MimeType_html } from '@nu-art/ts-common';
import { ModuleFE_VolatileImport } from '../ModuleFE_VolatileImport.js';
import './Component_DiseaseProfileReview.scss';
/**
 * "Review" section rendered into the DP editor (`disease-profile-review` slot). Shows a link that
 * opens/closes the HTML review overlay (Component_DiseaseProfileHtml) docked next to the editor. In
 * edit mode it also exposes attach/replace/remove controls for the DP HTML. Renders nothing outside
 * edit mode when the DP has no HTML. Open/close state and the sibling overlay are coordinated through
 * ModuleFE_VolatileImport (dp/frontend can't import this package), re-deriving on its dispatcher.
 * Mirrors Component_PathwayReview.
 */
export class Component_DiseaseProfileReview extends ComponentSync {
    htmlInputRef = createRef();
    __onDiseaseProfileReviewToggled = (reviewKey) => {
        if (reviewKey === `${this.props.tabId}::${this.props.diseaseProfileId}`)
            this.reDeriveState();
    };
    // Re-derive when this DP's cache entry changes so hasHtml (Attach vs Replace/Remove) and the link
    // stay in sync after an attach/replace/remove, without waiting for a parent re-render.
    __onDiseaseProfileUpdated = (...params) => {
        if (asArray(params[1]).every(dp => dp._id !== this.props.diseaseProfileId))
            return;
        this.reDeriveState();
    };
    deriveStateFromProps(nextProps, state) {
        const diseaseProfile = ModuleFE_DiseaseProfile.cache.unique(nextProps.diseaseProfileId);
        const importId = ModuleFE_VolatileImport.getImportIdForDiseaseProfile(nextProps.tabId, nextProps.diseaseProfileId);
        const pendingFile = importId ? ModuleFE_VolatileImport.getPendingDiseaseProfileHtmlFile(importId) : undefined;
        state.editMode = !!nextProps.editMode;
        state.hasHtml = !!(diseaseProfile?.diseaseProfileHtmlUrl || pendingFile);
        state.isOpen = ModuleFE_VolatileImport.isDiseaseProfileReviewOpen(nextProps.tabId, nextProps.diseaseProfileId);
        state.diseaseProfileLabel = diseaseProfile?.label ?? '';
        state.saving ??= false;
        state.confirmingRemove ??= false;
        // Drop the two-step Remove confirmation when leaving edit mode or switching to another DP/tab,
        // so it never lingers and re-appears without a fresh click.
        const slotChanged = nextProps.tabId !== this.props.tabId || nextProps.diseaseProfileId !== this.props.diseaseProfileId;
        if (!state.editMode || slotChanged)
            state.confirmingRemove = false;
        return state;
    }
    toggle = () => {
        const { tabId, diseaseProfileId } = this.props;
        this.state.isOpen
            ? ModuleFE_VolatileImport.closeDiseaseProfileReview(tabId, diseaseProfileId)
            : ModuleFE_VolatileImport.openDiseaseProfileReview(tabId, diseaseProfileId);
    };
    pickFile = () => this.htmlInputRef.current?.click();
    onFilePicked = async () => {
        const file = this.htmlInputRef.current?.files?.[0];
        if (!file)
            return;
        // Guard against a wrong pick (the `accept` filter is only a hint): reject non-HTML before the
        // upload so the user gets a clear message instead of a generic server error.
        const isHtml = /\.html?$/i.test(file.name) || file.type === MimeType_html;
        if (!isHtml) {
            ModuleFE_Toaster.toastError('Please choose an .html file.');
            if (this.htmlInputRef.current)
                this.htmlInputRef.current.value = '';
            return;
        }
        this.setState({ saving: true });
        try {
            await ModuleFE_DiseaseProfile.setDiseaseProfileHtml(this.props.diseaseProfileId, file);
            ModuleFE_Toaster.toastSuccess('HTML updated.');
        }
        catch (e) {
            ModuleFE_Toaster.toastError(e instanceof Error ? e.message : 'Failed to upload HTML.');
        }
        finally {
            // Reset so picking the same filename again still fires onChange.
            if (this.htmlInputRef.current)
                this.htmlInputRef.current.value = '';
            if (this.mounted)
                this.setState({ saving: false });
        }
    };
    confirmRemove = async () => {
        this.setState({ saving: true, confirmingRemove: false });
        try {
            await ModuleFE_DiseaseProfile.removeDiseaseProfileHtml(this.props.diseaseProfileId);
            ModuleFE_Toaster.toastSuccess('HTML removed.');
        }
        catch (e) {
            ModuleFE_Toaster.toastError(e instanceof Error ? e.message : 'Failed to remove HTML.');
        }
        finally {
            if (this.mounted)
                this.setState({ saving: false });
        }
    };
    renderActions = () => {
        if (this.state.saving)
            return _jsx("span", { className: 'disease-profile-review__status', children: "Working\u2026" });
        if (this.state.confirmingRemove)
            return _jsxs(_Fragment, { children: [_jsx("button", { type: 'button', className: 'disease-profile-review__btn disease-profile-review__btn--danger', onClick: this.confirmRemove, children: "Confirm remove" }), _jsx("button", { type: 'button', className: 'disease-profile-review__btn', onClick: () => this.setState({ confirmingRemove: false }), children: "Cancel" })] });
        return _jsxs(_Fragment, { children: [_jsx("button", { type: 'button', className: 'disease-profile-review__btn', onClick: this.pickFile, children: this.state.hasHtml ? 'Replace' : 'Attach HTML' }), this.state.hasHtml &&
                    _jsx("button", { type: 'button', className: 'disease-profile-review__btn disease-profile-review__btn--danger', onClick: () => this.setState({ confirmingRemove: true }), children: "Remove" })] });
    };
    render() {
        const editMode = this.state.editMode;
        if (!editMode && !this.state.hasHtml)
            return null;
        return _jsxs(TS_PropRenderer.Vertical, { label: 'Review:', className: 'disease-profile-review', children: [_jsxs(LL_H_C, { className: 'disease-profile-review__row', children: [this.state.hasHtml &&
                            _jsx("div", { className: 'disease-profile-review__link', onClick: this.toggle, children: this.state.diseaseProfileLabel || 'Open' }), editMode && this.renderActions()] }), _jsx("input", { ref: this.htmlInputRef, type: 'file', accept: '.html,.htm', style: { display: 'none' }, onChange: this.onFilePicked })] });
    }
}
//# sourceMappingURL=Component_DiseaseProfileReview.js.map